import { isResource } from '@medplum/core'
import type {
  CodeableConcept,
  Flag,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { codeableConcept } from '#src/codeableConcept.ts'
import { FLAG_NOTES_EXTENSION } from '#src/codeSystems.ts'
import { type DateString, dateStringSchema } from '#src/dates.ts'
import { ExtendedFlag, type FlagStatus } from '#src/models/flags/flag.ts'
import {
  type CriticalAttentionReason,
  formatStoredCriticalAttentionReason,
  formatStoredIsolationRequiredReason,
  formatStoredOosaReason,
  type IsolationRequiredReason,
  type OosaReason,
  PARTICIPANT_FLAGS,
  ParticipantFlagCode,
  type ParticipantFlagCode as ParticipantFlagCodeType,
  parseStoredCriticalAttentionReason,
  parseStoredIsolationRequiredReason,
  parseStoredOosaReason,
} from '#src/models/patients/index.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export interface OosaFlagDetails {
  startDate: DateString
  returnDate?: DateString
  returnDateUnknown?: boolean
  reason: OosaReason
  otherReason?: string
  notes: string
}

export interface CriticalAttentionFlagDetails {
  setOn: string
  reason: CriticalAttentionReason
  notes: string
}

export interface IsolationRequiredFlagDetails {
  setOn: string
  reason: IsolationRequiredReason
  notes: string
}

// Clinical flags other than dated absences live on Patient.meta.tag. OOSA
// also writes a Flag resource with a bounded period so the scheduling engine
// can hard-block the span.
export class PatientFlag extends ExtendedFlag {
  static isInstance(value: unknown): value is Flag {
    return isResource(value) && isOfType(value, 'Flag')
  }

  // Facesheet OOSA / Critical Attention / Isolation Required pills use this
  // active-only search so the date range can load with the Patient read
  // instead of a later waterfall.
  static byParticipant(patient: Reference<Patient>): QueryTypes {
    return {
      patient,
      status: 'active',
      _count: '100',
    }
  }

  // Flag.status is a single-value search param, so `active,inactive` matches
  // nothing. Omit status and let callers keep inactive OOSA Flags for History.
  static byOosa(patient: Reference<Patient>): QueryTypes {
    return {
      patient,
      _count: '100',
      _sort: '-_lastUpdated',
    }
  }

  static activeOosaFlags(flags: readonly PatientFlag[]): PatientFlag[] {
    return flags.filter(
      (flag) =>
        flag.status === 'active' && flag.code === ParticipantFlagCode.OOSA
    )
  }

  static findActiveOosa(
    flags: readonly PatientFlag[]
  ): PatientFlag | undefined {
    const actives = PatientFlag.activeOosaFlags(flags)
    if (actives.length === 0) {
      return undefined
    }
    return actives.reduce((latest, flag) =>
      oosaRecency(flag) >= oosaRecency(latest) ? flag : latest
    )
  }

  static activeCriticalAttentionFlags(
    flags: readonly PatientFlag[]
  ): PatientFlag[] {
    return flags.filter(
      (flag) =>
        flag.status === 'active' &&
        flag.code === ParticipantFlagCode.CRITICAL_ATTENTION
    )
  }

  static findActiveCriticalAttention(
    flags: readonly PatientFlag[]
  ): PatientFlag | undefined {
    const actives = PatientFlag.activeCriticalAttentionFlags(flags)
    if (actives.length === 0) {
      return undefined
    }
    return actives.reduce((latest, flag) =>
      oosaRecency(flag) >= oosaRecency(latest) ? flag : latest
    )
  }

  static activeIsolationRequiredFlags(
    flags: readonly PatientFlag[]
  ): PatientFlag[] {
    return flags.filter(
      (flag) =>
        flag.status === 'active' &&
        flag.code === ParticipantFlagCode.ISOLATION_REQUIRED
    )
  }

  static findActiveIsolationRequired(
    flags: readonly PatientFlag[]
  ): PatientFlag | undefined {
    const actives = PatientFlag.activeIsolationRequiredFlags(flags)
    if (actives.length === 0) {
      return undefined
    }
    return actives.reduce((latest, flag) =>
      oosaRecency(flag) >= oosaRecency(latest) ? flag : latest
    )
  }

  static build(params: {
    patient: Reference<Patient>
    author: Reference<Practitioner>
    flagCode: ParticipantFlagCodeType
    status?: FlagStatus
    period?: { start: DateString; end?: DateString }
    reason?: string
    notes?: string
  }): PatientFlag {
    const flagDefinition = PARTICIPANT_FLAGS[params.flagCode]

    const flag = new PatientFlag({
      subject: params.patient,
      author: params.author,
      code: codeableConcept(flagDefinition.coding),
      status: params.status ?? 'active',
      period: params.period,
    })
    if (params.reason) {
      flag.reason = params.reason
    }
    if (params.notes) {
      flag.notes = params.notes
    }
    flag.writeOosaNotes(params.notes ?? '')

    return flag
  }

  applyOosaDetails(details: OosaFlagDetails): void {
    this.start = details.startDate
    this.end = details.returnDateUnknown ? undefined : details.returnDate
    this.reason = formatStoredOosaReason(details.reason, details.otherReason)
    this.notes = details.notes
    this.writeOosaNotes(details.notes)
  }

  applyCriticalAttentionDetails(details: CriticalAttentionFlagDetails): void {
    if (!this.start) {
      this.start = details.setOn
    }
    this.reason = formatStoredCriticalAttentionReason(details.reason)
    this.notes = details.notes
    this.writeOosaNotes(details.notes)
  }

  applyIsolationRequiredDetails(details: IsolationRequiredFlagDetails): void {
    if (!this.start) {
      this.start = details.setOn
    }
    this.reason = formatStoredIsolationRequiredReason(details.reason)
    this.notes = details.notes
    this.writeOosaNotes(details.notes)
  }

  get oosaDetails(): OosaFlagDetails | undefined {
    if (this.code !== ParticipantFlagCode.OOSA) {
      return undefined
    }
    const startDate = dateStringSchema.safeParse(this.start)
    if (!startDate.success) {
      return undefined
    }
    const parsedReason = parseStoredOosaReason(this.reason)
    if (!parsedReason) {
      return undefined
    }
    const returnDateUnknown = this.end == null || this.end === ''
    const returnDate = dateStringSchema.safeParse(this.end)
    if (!returnDateUnknown && !returnDate.success) {
      return undefined
    }
    const details: OosaFlagDetails = {
      startDate: startDate.data,
      ...(returnDateUnknown
        ? { returnDateUnknown: true }
        : { returnDate: returnDate.data }),
      reason: parsedReason.reason,
      notes: this.oosaNotes,
    }
    if (parsedReason.otherReason) {
      details.otherReason = parsedReason.otherReason
    }
    return details
  }

  get criticalAttentionDetails(): CriticalAttentionFlagDetails | undefined {
    if (this.code !== ParticipantFlagCode.CRITICAL_ATTENTION) {
      return undefined
    }
    const reason = this.reason
      ? parseStoredCriticalAttentionReason(this.reason)
      : undefined
    if (!reason) {
      return undefined
    }
    const setOn = this.start || this.toFhirResource().meta?.lastUpdated
    if (!setOn) {
      return undefined
    }
    return {
      setOn,
      reason,
      notes: this.oosaNotes,
    }
  }

  get isolationRequiredDetails(): IsolationRequiredFlagDetails | undefined {
    if (this.code !== ParticipantFlagCode.ISOLATION_REQUIRED) {
      return undefined
    }
    const reason = this.reason
      ? parseStoredIsolationRequiredReason(this.reason)
      : undefined
    if (!reason) {
      return undefined
    }
    const setOn = this.start || this.toFhirResource().meta?.lastUpdated
    if (!setOn) {
      return undefined
    }
    return {
      setOn,
      reason,
      notes: this.oosaNotes,
    }
  }

  // Search results omit Flag.extension; _history keeps it. Persist notes on
  // category.text as well so the modal and hover card can read them back.
  get oosaNotes(): string {
    return this.notes || notesFromCategory(this.resource.category) || ''
  }

  private writeOosaNotes(notes: string): void {
    const otherCategories = (this.resource.category ?? []).filter(
      (concept) => !isOosaNotesCategory(concept)
    )
    if (notes) {
      this.resource.category = [...otherCategories, oosaNotesCategory(notes)]
      return
    }
    this.resource.category =
      otherCategories.length > 0 ? otherCategories : undefined
  }

  get participant(): Reference<Patient> {
    return this.subject
  }

  set participant(participant: Reference<Patient>) {
    this.subject = participant
  }
}

function oosaRecency(flag: PatientFlag): string {
  const lastUpdated = flag.toFhirResource().meta?.lastUpdated
  if (lastUpdated) {
    return lastUpdated
  }
  return flag.start ? `${flag.start}T23:59:59Z` : ''
}

function isOosaNotesCategory(concept: CodeableConcept): boolean {
  return (
    concept.coding?.some((coding) => coding.system === FLAG_NOTES_EXTENSION) ??
    false
  )
}

function notesFromCategory(
  categories: CodeableConcept[] | undefined
): string | undefined {
  return categories?.find(isOosaNotesCategory)?.text || undefined
}

function oosaNotesCategory(notes: string): CodeableConcept {
  return {
    coding: [{ system: FLAG_NOTES_EXTENSION, code: 'notes' }],
    text: notes,
  }
}
